import clsx from 'clsx';
import { uniqueId } from 'lodash-es';
import {
  type PropsWithChildren,
  type ReactElement,
  type JSX,
  useMemo,
  Children,
} from 'react';
import { useIntl } from 'react-intl';
import { isElement } from 'react-is';

import Borders from '@dynatrace/strato-design-tokens/borders';
import Spacings from '@dynatrace/strato-design-tokens/spacings';
import {
  ChevronDownSmallIcon,
  ChevronRightSmallIcon,
} from '@dynatrace/strato-icons';

import { getRowDetailsRowModel } from './details-row-model.js';
import type {
  DataTableExpandableRowProps,
  DataTableExpandableRowWrapperProps,
  DataTableRowDetailState,
} from './row-details-types.js';
import { rowDetailsWrapperCSS } from './RowDetails.sty.js';
import { Button } from '../../../../buttons/button/Button.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { DataTableDefaultCell } from '../../components/DataTableDefaultCell.js';
import { DATA_TABLE_ROW_DETAILS_COLUMN_ID } from '../../constants.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import type {
  ColumnDef,
  InitialTableState,
  Row,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';
import { assignRowMethods } from '../../hooks/useTable/utils.js';
import { dataTableMessages } from '../../messages.js';
import type { DataTableRowData } from '../../public.api.js';

/**
 * DataTable slot selection for expandable rows.
 * @public
 */
export function DataTableExpandableRowTemplate<TData extends DataTableRowData>(
  props: DataTableExpandableRowProps<TData>,
) {
  return null;
}

/**
 * Wrapper helper for the DataTable.ExpandableRow that will automatically sync
 * specific style properties like row padding for rowDensity variant settings.
 * @public
 */
export function DataTableExpandableRowWrapper(
  props: DataTableExpandableRowWrapperProps,
) {
  const {
    className: consumerClassName,
    'data-testid': dataTestId,
    style: consumerStyle,
    ...remainingProps
  } = props;
  const table = useDataTable();
  return (
    <div
      data-testid={dataTestId}
      {...remainingProps}
      className={clsx(
        consumerClassName,
        rowDetailsWrapperCSS({
          rowDensity: table.getVisualVariantOptions().rowDensity,
        }),
      )}
      style={consumerStyle}
    />
  );
}

/**
 * Type guard to ensure that a react element is a `DataTable.ExpandableRow` element.
 * @internal
 */
function isDataTableExpandableRowTemplate<TData extends DataTableRowData>(
  child: unknown,
): child is ReactElement<DataTableExpandableRowProps<TData>> {
  return isElement(child) && child.type === DataTableExpandableRowTemplate;
}

/**
 * Row details feature for the DataTable.
 * @internal
 */
export const DataTableRowDetails: TableFeature = {
  getInitialState(state?: InitialTableState) {
    return {
      rowDetails: {},
      ...state,
    };
  },

  createTable<TData extends DataTableRowData>(table: Table<TData>) {
    /**
     * Internal identification id for the table instance to connect aria-controls
     * between the expander button and the row details.
     * @internal
     */
    table.rowDetailsTableId = uniqueId('DataTable-');

    /**
     * Allows access to the row model coming before the row details.
     */
    table.getPreRowDetailsRowModel = () => table.getPaginationRowModel();

    /**
     * Allows access to the row details model if available.
     */
    table.getRowDetailsRowModel = () => {
      if (
        !table._getRowDetailsRowModel &&
        table.options.getRowDetailsRowModel
      ) {
        table._getRowDetailsRowModel =
          table.options.getRowDetailsRowModel(table);
      }

      if (!table._getRowDetailsRowModel) {
        return table.getPreRowDetailsRowModel();
      }

      return table._getRowDetailsRowModel();
    };

    assignRowMethods(table, {
      /**
       * Determines if the details row for a particular row is open.
       */
      getIsDetailsOpen(this: Row<TData>) {
        const detailOpenState = table.getState().rowDetails ?? false;

        if (typeof detailOpenState === 'boolean') {
          return detailOpenState;
        }
        return detailOpenState[this.id] === true;
      },

      /**
       * Defines the grid position for the row details row.
       */
      getRowDetailsGridPosition() {
        return {
          gridColumnStart: 1,
          gridColumnEnd: `span ${table.getVisibleLeafColumns().length}`,
        };
      },

      /**
       * Returns the aria control id to connect the expanding button to the
       * row details wrapper.
       */
      getRowDetailsAriaControlsId(this: Row<TData>) {
        return `${table.rowDetailsTableId}-${this.id}-expand`;
      },

      /**
       * Determines if the details for this row can be shown.
       */
      getCanOpenDetails(this: Row<TData>) {
        if (typeof table.options.rowDetailsDisableRow === 'function') {
          return !table.options.rowDetailsDisableRow(this.original);
        }
        return true;
      },

      /**
       * Control command to toggle the state of the rowDetails.
       */
      toggleDetails(this: Row<TData>) {
        // remember current scroll position
        const scrollOffset = table.getRowScrollOffset();

        // expand/collapse details
        table.options.onRowDetailsOpenChange?.(
          (prev: DataTableRowDetailState) => {
            let newRowDetailsState: DataTableRowDetailState = {};
            if (typeof prev === 'boolean' && prev === true) {
              const flatRows = table.getRowModel().flatRows;
              for (const flatRow of flatRows) {
                if (this.getCanOpenDetails() && this.id !== flatRow.id) {
                  newRowDetailsState[flatRow.id] = true;
                }
              }
              return newRowDetailsState;
            }

            newRowDetailsState = { ...prev };
            if (newRowDetailsState[this.id] === true) {
              delete newRowDetailsState[this.id];
            } else {
              newRowDetailsState[this.id] = true;
            }
            return newRowDetailsState;
          },
        );

        // scroll back to original scroll position to avoid row jumping
        requestAnimationFrame(() => {
          table.scrollToRowOffset(scrollOffset);
        });
      },
    });
  },
};

/**
 * Helper function to identify the ROW_DETAILS_COLUMN
 * @internal
 */
export function isRowDetailsColumnDef<TData extends DataTableRowData>(
  column: ColumnDef<TData>,
): boolean {
  return column.id === ROW_DETAILS_COLUMN.id;
}

function RowDetailsCell(props: Readonly<{ row: Row<unknown> }>): JSX.Element {
  const { row } = props;
  const intl = useIntl();

  const isOpen = row.getIsDetailsOpen();
  const Icon = isOpen ? <ChevronDownSmallIcon /> : <ChevronRightSmallIcon />;

  return (
    <DataTableDefaultCell>
      <Button
        size="condensed"
        variant="default"
        style={{
          borderRadius: Borders.Radius.Field.Subdued,
          paddingLeft: Spacings.Size2,
          paddingRight: Spacings.Size2,
          paddingTop: 0,
          paddingBottom: 0,
        }}
        aria-label={intl.formatMessage(dataTableMessages.rowDetailsTrigger, {
          isDetailsOpen: isOpen,
          id: row.id,
        })}
        aria-expanded={isOpen}
        aria-controls={isOpen ? row.getRowDetailsAriaControlsId() : undefined}
        onClick={() => row.toggleDetails()}
        disabled={!row.getCanOpenDetails()}
      >
        <Button.Prefix>{Icon}</Button.Prefix>
      </Button>
    </DataTableDefaultCell>
  );
}

/**
 * Column definition for the row details column.
 * @internal
 */
export const ROW_DETAILS_COLUMN: ColumnDef<unknown, unknown> = {
  id: DATA_TABLE_ROW_DETAILS_COLUMN_ID,
  alignment: 'left',
  accessorFn: () => '',
  header: () => {
    return '';
  },
  width: 'content',
  enableSorting: false,
  enableResizing: false,
  enableUserActions: false,
  enableHiding: false,
  meta: {
    builtin: true,
    builtinPlacement: 'prefix',
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
    cellDeps: (cell) => {
      return [cell.row.getIsDetailsOpen()];
    },
  },
  cell: ({ row }) => <RowDetailsCell row={row} />,
};

/**
 * Configuration hook for expandable rows of the DataTable.
 * @internal
 */
export function useRowDetails<TData extends DataTableRowData>(
  props: PropsWithChildren,
  options: TableOptions<TData>,
): void {
  const { children } = props;
  const rowDetailsTemplateProps = useMemo(() => {
    for (const child of Children.toArray(children)) {
      if (isDataTableExpandableRowTemplate<TData>(child)) {
        return child?.props;
      }
    }
  }, [children]);

  const [openRowDetails, setOpenRowDetails] = useControllableState({
    value: rowDetailsTemplateProps?.expandedRows,
    defaultValue: rowDetailsTemplateProps?.defaultExpandedRows,
    onChange: (value) => {
      if (value !== true) {
        rowDetailsTemplateProps?.onExpandedRowsChange?.(value);
      }
    },
  });

  const template = rowDetailsTemplateProps?.children;
  if (template !== undefined && typeof template === 'function') {
    options.enableRowDetails = true;
    options.rowDetailsTemplate = template;
    options.getRowDetailsRowModel = getRowDetailsRowModel();
    options.rowDetailsDisableRow = rowDetailsTemplateProps?.disableExpand;

    options.state = {
      ...options.state,
      rowDetails: openRowDetails,
    };
    options.onRowDetailsOpenChange = setOpenRowDetails;
  }
}
